import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dmClient } from '../../services/dmClient';
import usePageTitle from '../../hooks/usePageTitle';
import { money } from './caseBuilderModel';
import './caseBuilder.css';

const SCOPES = [
  { id: 'mine', label: 'Mine' },
  { id: 'shared', label: 'Shared with me' },
];
const PAGE_SIZES = [50, 100, 200];
const SORT_COLUMNS = [
  { id: 'case_id', label: 'Case id' },
  { id: 'payer', label: 'Payer' },
  { id: 'claim', label: 'Claim' },
  { id: 'patient', label: 'Patient' },
  { id: 'service_date', label: 'Service date' },
  { id: 'denial', label: 'Denial' },
  { id: 'billed', label: 'Billed' },
  { id: 'appeal_by', label: 'Appeal by' },
  { id: 'created_at', label: 'Created Date' },
  { id: 'created_by', label: 'Created By' },
  { id: 'expected', label: 'Expected' },
  { id: 'pipeline', label: 'Pipeline' },
];
const COLUMN_WIDTHS = {
  case_id: 96,
  payer: 150,
  claim: 120,
  patient: 110,
  service_date: 118,
  denial: 120,
  billed: 96,
  appeal_by: 110,
  created_at: 124,
  created_by: 180,
  expected: 104,
  pipeline: 110,
  actions: 240,
};
const MIN_COLUMN_WIDTH = 72;

function Clip({ children, title, className = '' }) {
  return <div className={`cb-clip${className ? ` ${className}` : ''}`} title={title || undefined}>{children}</div>;
}

export default function CaseBuilderListPage() {
  usePageTitle('Case Builder');
  const navigate = useNavigate();
  const [scope, setScope] = useState('mine');
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [sort, setSort] = useState('service_date');
  const [sortDir, setSortDir] = useState('desc');
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [widths, setWidths] = useState(COLUMN_WIDTHS);
  const [resizing, setResizing] = useState(false);
  const widthsRef = useRef(widths);
  const dragRef = useRef(null);

  useEffect(() => { widthsRef.current = widths; }, [widths]);
  useEffect(() => () => {
    const drag = dragRef.current;
    if (!drag) return;
    window.removeEventListener('pointermove', drag.move);
    window.removeEventListener('pointerup', drag.up);
    document.body.classList.remove('cb-resizing');
  }, []);

  function startResize(event, columnId) {
    if (event.button != null && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = widthsRef.current[columnId];
    function move(ev) {
      const next = Math.max(MIN_COLUMN_WIDTH, Math.round(startWidth + ev.clientX - startX));
      setWidths((current) => ({ ...current, [columnId]: next }));
    }
    function up() {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.classList.remove('cb-resizing');
      dragRef.current = null;
      setResizing(false);
    }
    dragRef.current = { move, up };
    document.body.classList.add('cb-resizing');
    setResizing(true);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  const load = useCallback(async (nextScope, nextQuery, nextPage, nextPageSize, nextSort, nextDir) => {
    setError('');
    try {
      const res = await dmClient.get('/case-builder/cases', {
        params: {
          scope: nextScope,
          q: nextQuery,
          page: nextPage,
          page_size: nextPageSize,
          sort: nextSort,
          dir: nextDir,
        },
      });
      const data = res.data?.data || {};
      const rows = data.items || [];
      const count = data.total || 0;
      if (rows.length === 0 && nextPage > 1 && count > 0) {
        setPage(nextPage - 1);
        return;
      }
      setItems(rows);
      setTotal(count);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load cases.');
    }
  }, []);

  useEffect(() => { load(scope, appliedQuery, page, pageSize, sort, sortDir); }, [scope, appliedQuery, page, pageSize, sort, sortDir, load]);

  function toggleSort(column) {
    setPage(1);
    if (sort === column) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSort(column);
    setSortDir('asc');
  }

  async function createCase() {
    setBusy(true);
    try {
      const res = await dmClient.post('/case-builder/cases');
      navigate(`/case-builder/${res.data.data.canonical_case_id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create a case.');
    } finally {
      setBusy(false);
    }
  }

  async function processCase(id) {
    setBusy(true);
    setError('');
    try {
      const res = await dmClient.post(`/case-builder/cases/${id}/executions`);
      navigate(`/case-builder/${id}/runs/${res.data.data.claim_resolution_execution_id}`, { state: { watch: true } });
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start this run.');
      setBusy(false);
    }
  }

  async function duplicate(id) {
    setBusy(true);
    try {
      const res = await dmClient.post(`/case-builder/cases/${id}/duplicate`);
      navigate(`/case-builder/${res.data.data.canonical_case_id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not duplicate the case.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmPending() {
    if (!pending) return;
    setBusy(true);
    try {
      await dmClient.delete(`/case-builder/cases/${pending.item.canonical_case_id}`);
      await load(scope, appliedQuery, page, pageSize, sort, sortDir);
      setPending(null);
    } catch (err) {
      setError(err.response?.data?.error || 'The request failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cb-page">
      <header className="cb-head">
        <div>
          <h1>Case Builder</h1>
          <p>Canonical denial cases for your login. IMG-001 and LAB-001 are added if this login does not already have them..</p>
        </div>
        <div className="cb-head-actions">
          <Link to="/case-builder/reference-instances" className="dm-btn dm-btn--secondary">Instance mapping</Link>
          <Link to="/case-builder/reference-rules" className="dm-btn dm-btn--secondary">Reference mapping</Link>
          <button type="button" className="dm-btn dm-btn--primary" onClick={createCase} disabled={busy}>New case</button>
        </div>
      </header>

      <div className="cb-toolbar">
        <div className="cb-chips">
          {SCOPES.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`cb-chip${scope === item.id ? ' is-on' : ''}`}
              onClick={() => { setScope(item.id); setPage(1); }}
            >
              {item.label}
            </button>
          ))}
        </div>
        <form
          className="cb-search"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setAppliedQuery(query.trim());
          }}
        >
          <input
            value={query}
            placeholder="Payer, claim, patient, or case id"
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search cases"
          />
          <button type="submit" className="dm-btn dm-btn--secondary">Search</button>
        </form>
      </div>

      {error ? <p className="cb-error">{error}</p> : null}

      <div className="cb-table-wrap">
        <table
          className={`cb-table cb-table--sized${resizing ? ' is-resizing' : ''}`}
          style={{ width: Object.values(widths).reduce((sum, width) => sum + width, 0) }}
        >
          <colgroup>
            {SORT_COLUMNS.map((column) => (
              <col key={column.id} style={{ width: widths[column.id] }} />
            ))}
            <col style={{ width: widths.actions }} />
          </colgroup>
          <thead>
            <tr>
              {SORT_COLUMNS.map((column) => (
                <th
                  key={column.id}
                  aria-sort={sort === column.id ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <button
                    type="button"
                    className={`cb-sort${sort === column.id ? ' is-on' : ''}`}
                    onClick={() => toggleSort(column.id)}
                  >
                    {column.label}
                    {sort === column.id ? (sortDir === 'asc' ? ' \u2191' : ' \u2193') : ''}
                  </button>
                  <span
                    className="cb-resize"
                    role="separator"
                    aria-orientation="vertical"
                    aria-label={`Resize ${column.label}`}
                    onPointerDown={(event) => startResize(event, column.id)}
                  />
                </th>
              ))}
              <th>
                <span
                  className="cb-resize"
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize actions"
                  onPointerDown={(event) => startResize(event, 'actions')}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={SORT_COLUMNS.length + 1}>No cases in this view.</td></tr>
            ) : items.map((item) => (
              <tr key={item.canonical_case_id}>
                <td><Clip title={item.case_key}>{item.case_key || '—'}</Clip></td>
                <td>
                  <Clip title={item.payer_name}><span className="cb-strong">{item.payer_name || '—'}</span></Clip>
                </td>
                <td>
                  <Clip title={item.claim_id}>{item.claim_id || '—'}</Clip>
                  <Clip title={item.claim_form} className="cb-sub">{item.claim_form}</Clip>
                </td>
                <td><Clip title={item.patient_id}>{item.patient_id || '—'}</Clip></td>
                <td><Clip title={item.date_of_service}>{item.date_of_service || '—'}</Clip></td>
                <td>
                  <Clip>{`${item.denied_line_count || 0} denied`}</Clip>
                  <Clip className="cb-sub">{[item.first_carc, item.first_rarc].filter(Boolean).join(' / ') || '—'}</Clip>
                </td>
                <td><Clip>{money(item.total_billed)}</Clip></td>
                <td><Clip title={item.appeal_deadline}>{item.appeal_deadline || '—'}</Clip></td>
                <td><Clip title={item.created_at}>{item.created_at || '—'}</Clip></td>
                <td><Clip title={item.created_by}>{item.created_by || '—'}</Clip></td>
                <td>
                  <Clip title={item.final_disposition}>{item.final_disposition || '—'}</Clip>
                  <Clip title={item.analysis_status} className="cb-sub">{item.analysis_status}</Clip>
                </td>
                <td>
                  <span className={`cb-badge${item.status === 'queued' ? ' is-queued' : ''}`}>
                    {item.status === 'queued' ? 'Queued' : 'Draft'}
                  </span>
                  {item.latest_execution_status ? (
                    <div className="cb-sub">{String(item.latest_execution_status).replace(/_/g, ' ')}</div>
                  ) : null}
                </td>
                <td>
                  <div className="cb-actions">
                    <button type="button" onClick={() => navigate(`/case-builder/${item.canonical_case_id}`)}>Edit</button>
                    <button type="button" onClick={() => duplicate(item.canonical_case_id)} disabled={busy}>Duplicate</button>
                    <button type="button" onClick={() => processCase(item.canonical_case_id)} disabled={busy}>Process</button>
                    {item.is_owner ? (
                      <button type="button" className="danger" onClick={() => setPending({ kind: 'delete', item })}>Delete</button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="cb-pager">
        <span>
          {total === 0 ? '0 cases' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`}
        </span>
        <label>
          Per page
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            aria-label="Cases per page"
          >
            {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>
        <button type="button" className="dm-btn dm-btn--secondary" onClick={() => setPage((n) => n - 1)} disabled={page <= 1}>Previous</button>
        <button type="button" className="dm-btn dm-btn--secondary" onClick={() => setPage((n) => n + 1)} disabled={page * pageSize >= total}>Next</button>
      </div>

      {pending ? (
        <div className="cb-modal-back" role="presentation">
          <div className="cb-modal" role="dialog" aria-modal="true">
            <h2>Delete this case?</h2>
            <p>
              {`${pending.item.payer_name || 'This payer'} · claim ${pending.item.claim_id || pending.item.case_key} will be removed from your company.`}
            </p>
            <div className="cb-modal__actions">
              <button type="button" className="dm-btn dm-btn--secondary" onClick={() => setPending(null)}>Cancel</button>
              <button type="button" className="dm-btn dm-btn--primary" onClick={confirmPending} disabled={busy}>Delete</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
